import { useQuery } from '@tanstack/react-query'
import { Dices, Play, Plus, RotateCcw, Save, Undo2, Wand2 } from 'lucide-react'
import { useMemo, useState } from 'react'

import { MAX_SCENERY_ITEMS, sceneryCandidatesQuery, type SceneryCandidate, type SceneryItem } from '@/api/stages'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/components/ui/utils'

import { AddSceneryDialog, CANDIDATE_PAGE } from './AddSceneryDialog'
import { SceneryCanvas } from './SceneryCanvas'
import { SceneryInspector } from './SceneryInspector'
import { byZ, itemFromCandidate, itemThumb, normalizeZ } from './scenery'
import type { SceneryEditor } from './useSceneryEditor'
import { BAND_SHORT, clipLabel } from './words'

/**
 * The Scenery tab: lay out the living things drawn between the picture and the actors
 * (docs/SKEL-STORY.md §6 Q7). Until someone saves a layout the studio picks the scenery for each
 * story by itself ("automatic") — the tab then shows one example of what it would pick; changing
 * anything makes it your own layout. Add things from the catalog, drag them, resize, flip, choose
 * how they move, reorder, remove; "Play preview" plays the unsaved layout, "Save" keeps it,
 * "Back to automatic" throws the layout away.
 */

export interface SceneryTabProps {
  uid: string
  picture: string
  editor: SceneryEditor
  onPreview(): void
}

export function SceneryTab({ uid, picture, editor, onPreview }: SceneryTabProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [confirmAuto, setConfirmAuto] = useState(false)
  const { draft, shown } = editor

  // Every candidate, once (the same request as the Add dialog's first page): the pictures and
  // clip lists of things already placed.
  const all = useQuery(sceneryCandidatesQuery({ stage_uid: uid, limit: CANDIDATE_PAGE }))
  const byUid = useMemo(() => {
    const m = new Map<string, SceneryCandidate>()
    for (const c of all.data ?? []) m.set(c.puppet_uid, c)
    return m
  }, [all.data])

  const selected = shown.find((i) => i.id === selectedId) ?? null
  const change = (items: SceneryItem[]) => draft.set(items)

  const full = shown.length >= MAX_SCENERY_ITEMS
  const add = (c: SceneryCandidate) => {
    if (full) return
    const item = itemFromCandidate(c, shown)
    // Tidy the order too: a layout read back can carry any z, and the server takes -50…50.
    draft.set(normalizeZ([...shown, item]))
    setSelectedId(item.id)
  }

  if (editor.pending) return <p className="card p-6 text-ink-2">Opening the scenery…</p>
  if (editor.error) {
    return (
      <div className="card flex flex-col items-start gap-3 p-6" role="alert">
        <p className="text-ink">The scenery could not be loaded.</p>
        <Button variant="secondary" onClick={editor.retry}>
          Try again
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4" data-testid="scenery-tab">
      <StatusLine editor={editor} />

      {editor.conflict ? (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl border border-warn/40 bg-warn-soft px-4 py-3 text-sm" data-testid="scenery-conflict">
          <span className="flex-1 text-ink">Someone saved this scenery while you were working on it.</span>
          <Button size="sm" variant="secondary" onClick={editor.useTheirs}>
            Use theirs
          </Button>
          <Button size="sm" variant="primary" onClick={editor.keepMine} loading={editor.saving}>
            Keep mine
          </Button>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2" role="toolbar" aria-label="Scenery">
        <Button variant="primary" onClick={() => setAdding(true)} disabled={full} title={full ? `A background holds at most ${MAX_SCENERY_ITEMS} things — remove one to add another.` : undefined} data-testid="add-thing">
          <Plus aria-hidden />
          Add a thing
        </Button>
        <Button variant="secondary" onClick={onPreview} data-testid="scenery-preview">
          <Play aria-hidden />
          Play preview
        </Button>
        <Button variant="secondary" onClick={editor.startFromSuggestion} loading={editor.suggesting} data-testid="start-suggestion">
          {editor.suggesting ? null : <Dices aria-hidden />}
          Start from a suggestion
        </Button>
        <Button variant="ghost" onClick={draft.undo} disabled={!draft.canUndo} data-testid="undo">
          <Undo2 aria-hidden />
          Undo
        </Button>
        <div className="ml-auto flex flex-wrap gap-2">
          {editor.mode === 'custom' ? (
            <Button variant="ghost" onClick={() => setConfirmAuto(true)} data-testid="back-to-auto">
              <Wand2 aria-hidden />
              Back to automatic
            </Button>
          ) : null}
          {editor.dirty ? (
            <Button
              variant="ghost"
              onClick={() => {
                draft.discard()
                setSelectedId(null)
              }}
              data-testid="discard"
            >
              <RotateCcw aria-hidden />
              Discard changes
            </Button>
          ) : null}
          <Button variant="primary" onClick={editor.save} disabled={!editor.dirty || editor.conflict} loading={editor.saving} data-testid="save-scenery">
            {editor.saving ? null : <Save aria-hidden />}
            Save
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="flex min-w-0 flex-col gap-2">
          <SceneryCanvas
            picture={picture}
            items={shown}
            selectedId={selectedId}
            candidates={byUid}
            onSelect={setSelectedId}
            onGestureStart={draft.begin}
            onLive={draft.live}
            onGestureEnd={draft.end}
            onChange={change}
          />
          <p className="text-xs text-ink-3">
            Drag a thing to move it; drag the round handle to make it bigger or smaller. Arrow keys nudge the selected thing, Delete removes it.
            {full ? ` That is the most a background can hold (${MAX_SCENERY_ITEMS}) — remove one to add another.` : ''}
          </p>
        </div>

        <aside className="card flex min-w-0 flex-col gap-4 p-4" aria-label="Scenery details">
          {selected ? (
            <SceneryInspector
              key={selected.id}
              item={selected}
              items={shown}
              candidate={byUid.get(selected.puppet_uid)}
              candidates={byUid}
              onChange={change}
              onGestureStart={draft.begin}
              onLive={draft.live}
              onGestureEnd={draft.end}
              onRemoved={() => setSelectedId(null)}
            />
          ) : (
            <p className="text-sm text-ink-2">{shown.length ? 'Pick a thing on the picture or in the list to change it.' : 'Nothing here yet — add a tree, a cloud or a flower.'}</p>
          )}
          <ThingList items={shown} selectedId={selectedId} candidates={byUid} onSelect={setSelectedId} />
        </aside>
      </div>

      <AddSceneryDialog open={adding} onOpenChange={setAdding} stageUid={uid} onPick={add} />

      <Dialog open={confirmAuto} onOpenChange={setConfirmAuto}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Go back to automatic scenery?</DialogTitle>
            <DialogDescription>Your layout for this background is removed, and each new story picks its own scenery again. Stories already made keep theirs.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirmAuto(false)}>
              Keep my layout
            </Button>
            <Button
              variant="danger"
              loading={editor.resetting}
              onClick={() =>
                void editor.backToAuto().then(() => {
                  setConfirmAuto(false)
                  setSelectedId(null)
                })
              }
              data-testid="confirm-auto"
            >
              Back to automatic
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function StatusLine({ editor }: { editor: SceneryEditor }) {
  const n = editor.shown.length
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="scenery-status">
      <Badge tone={editor.mode === 'custom' ? 'accent' : 'neutral'}>{editor.mode === 'custom' ? 'Your layout' : 'Automatic'}</Badge>
      <span className="text-ink-2">
        {editor.dirty
          ? 'You have changes that are not saved yet.'
          : editor.showingExample
            ? editor.exampleLoading
              ? 'Asking the studio what it would pick…'
              : n === 0
                ? 'Each story picks its own scenery. Add a thing to make your own layout.'
                : 'Each story picks its own scenery — this is one example. Change anything to make it your own.'
            : `${n === 1 ? '1 thing' : `${n} things`} live in this background.`}
      </span>
    </div>
  )
}

function ThingList({ items, selectedId, candidates, onSelect }: { items: readonly SceneryItem[]; selectedId: string | null; candidates: ReadonlyMap<string, SceneryCandidate>; onSelect(id: string): void }) {
  if (items.length === 0) return null
  // Front first, like layers.
  const rows = byZ(items).reverse()
  return (
    <div className="flex flex-col gap-1.5">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-3">In this background ({items.length})</h3>
      <ul className="flex flex-col gap-1" data-testid="thing-list">
        {rows.map((it) => {
          const thumb = itemThumb(it, candidates)
          return (
            <li key={it.id}>
              <button
                type="button"
                onClick={() => onSelect(it.id)}
                aria-pressed={it.id === selectedId}
                className={cn('flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors focus-visible:focus-ring', it.id === selectedId ? 'bg-accent-soft text-ink' : 'text-ink-2 hover:bg-paper-2')}
              >
                <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-md bg-paper-2">{thumb ? <img src={thumb} alt="" className="size-full object-contain" /> : null}</span>
                <span className="min-w-0 flex-1 truncate">{it.name}</span>
                <span className="shrink-0 text-xs text-ink-3">
                  {BAND_SHORT[it.band]}
                  {it.clip ? ` · ${clipLabel(it.clip).toLowerCase()}` : ''}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
